// ─────────────────────────────────────────────────────────────
// ZIFEK — Assistant IA : réservation de rendez-vous (ZCalendar)
// Permet au chat IA de comprendre « réserve-moi un dentiste à Fès
// mardi à 17h30 », de vérifier le calendrier ZCalendar du commerce
// et de réserver le créneau demandé — ou de proposer le plus proche.
// Gère aussi l'affichage des rendez-vous à venir du client.
// ─────────────────────────────────────────────────────────────

import { supabase } from '@/lib/supabase';
import { pushNotification } from '@/lib/notifications';
import {
  computeAvailableSlots,
  parseTime,
  toDateStr,
  formatDateFr,
  type BookingService,
  type BookingAvailability,
  type BookingBreak,
  type BookingHoliday,
  type BookingAppointment,
} from '@/lib/booking';

// ── Détection d'intention ────────────────────────────────────

/** « j'ai quel rendez-vous prévu ? », « mes RDV », « montre mes réservations » */
export function isAppointmentsRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (/(quel|quels|quelle|quelles|combien).{0,25}(rendez|rdv|r[ée]servation)/.test(t)) return true;
  if (/(mes|mon|j'ai|jai).{0,22}(rendez[- ]?vous|rdv|r[ée]servations?)/.test(t)) return true;
  if (/(rendez[- ]?vous|rdv|r[ée]servations?)\s+(pr[ée]vu|prevus?|à venir|a venir)/.test(t)) return true;
  return false;
}

/** « réserve-moi un dentiste à Fès mardi 17h30 », « prendre un RDV » */
export function isBookingRequest(text: string): boolean {
  if (isAppointmentsRequest(text)) return false;
  const t = text.toLowerCase();
  return /(r[ée]serv|reserv|booker|booking|prends?\s+(un\s+)?(rendez|rdv)|prendre\s+(un\s+)?(rendez|rdv)|rendez[- ]?vous)/.test(t);
}

/** « annule mon RDV de mardi », « supprime mon rendez-vous dentiste » */
export function isCancelAppointmentRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (!/(rendez[- ]?vous|rdv|r[ée]servation)/.test(t)) return false;
  return /(annul|supprim|effac|r[ée]sili|d[ée]sinscri|enl[èe]ve)/.test(t);
}

/** « déplace mon RDV à jeudi 15h », « reporte mon rendez-vous » */
export function isRescheduleAppointmentRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (!/(rendez[- ]vous|rdv|r[ée]servation)/.test(t)) return false;
  return /(d[ée]plac|reporte|replanifi|d[ée]cale|decale|repousse|d[ée]cal|change|modifie)/.test(t);
}

// ── Annulation / déplacement ────────────────────────────────

export interface ParsedReschedule {
  /** Jour/date du rendez-vous à déplacer (source). */
  sourceDate: string | null;
  sourceDayLabel: string | null;
  /** Nouveau jour/date souhaité (cible). */
  targetDate: string | null;
  targetDayLabel: string | null;
  targetTime: string | null;
}

/**
 * Analyse un ordre de déplacement du type « déplace mon RDV de mardi à jeudi
 * 15h » : la partie avant le mot de transition (à / au / vers / pour) décrit le
 * rendez-vous actuel, la partie après décrit le créneau souhaité.
 */
export function parseReschedule(text: string): ParsedReschedule {
  const m = text.match(/\s(?:à|a|au|vers|pour)\s/i);
  let sourcePart = text;
  let targetPart = text;
  if (m && m.index != null) {
    sourcePart = text.slice(0, m.index);
    targetPart = text.slice(m.index + m[0].length);
  }
  const src = parseRequestedWhen(sourcePart);
  const tgt = parseRequestedWhen(targetPart);
  return {
    sourceDate: src.date,
    sourceDayLabel: src.dayLabel,
    targetDate: tgt.date,
    targetDayLabel: tgt.dayLabel,
    targetTime: tgt.time,
  };
}

const FR_WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** Nom du jour (fr) d'une date « AAAA-MM-JJ ». */
function weekdayFr(dateStr: string): string {
  try { return FR_WEEKDAYS[new Date(`${dateStr}T00:00:00`).getDay()]; } catch { return ''; }
}

/**
 * Filtre les rendez-vous du client selon le jour cité. On compare le jour de la
 * semaine (et non la date exacte) pour retrouver « mardi » quel que soit le
 * nombre de semaines d'écart.
 */
export function matchAppointments(
  appointments: ClientAppointment[],
  when: { date: string | null; dayLabel: string | null },
): ClientAppointment[] {
  if (!when.date && !when.dayLabel) return appointments;
  const wd = when.date ? weekdayFr(when.date) : (when.dayLabel || '').toLowerCase();
  if (!wd) return appointments;
  const matched = appointments.filter((a) => weekdayFr(a.date) === wd);
  return matched.length > 0 ? matched : [];
}

/**
 * Prévient le commerce (cloche de notification + e-mail) qu'un de ses
 * rendez-vous a été annulé ou déplacé par le client.
 * Best-effort : n'échoue jamais et ne bloque pas l'action métier.
 */
async function notifyMerchantAppointmentChange(
  appointment: ClientAppointment,
  change: { kind: 'cancelled' | 'rescheduled'; newDate?: string; newTime?: string },
): Promise<void> {
  try {
    const { data: svcRow } = await supabase
      .from('booking_services')
      .select('id, service_name, owner')
      .eq('id', appointment.serviceId)
      .maybeSingle();
    const ownerId = Number(svcRow?.owner) || 0;
    if (!ownerId) return;

    const oldWhen = `${formatDateFr(appointment.date)} à ${appointment.time}`;
    const message = change.kind === 'cancelled'
      ? `RDV annulé par le client : ${appointment.serviceName}, le ${oldWhen}.`
      : `RDV déplacé par le client : ${appointment.serviceName}, nouveau créneau le ${formatDateFr(change.newDate || appointment.date)} à ${change.newTime || appointment.time} (ancien : ${oldWhen}).`;

    // 1) Cloche de notification du marchand.
    await pushNotification({ userId: ownerId, type: 'booking', message });

    // 2) E-mail de courtoisie (best-effort).
    const { data: owner } = await supabase
      .from('users')
      .select('email')
      .eq('id', ownerId)
      .maybeSingle();
    const to = (owner?.email as string) || '';
    if (to) {
      void supabase.functions.invoke('send-booking-notification', {
        body: {
          to,
          type: change.kind === 'cancelled' ? 'cancelled_booking' : 'rescheduled_booking',
          service_name: appointment.serviceName,
          date: change.newDate || appointment.date,
          time: change.newTime || appointment.time,
          previous_date: appointment.date,
          previous_time: appointment.time,
        },
      });
    }
  } catch {
    /* best-effort : on ignore les erreurs de notification */
  }
}

/** Annule un rendez-vous (statut « cancelled ») et prévient le commerce. */
export async function cancelClientAppointment(appointment: ClientAppointment): Promise<void> {
  const { error } = await supabase
    .from('booking_appointments')
    .update({ status: 'cancelled' })
    .eq('id', appointment.id);
  if (error) throw error;
  void notifyMerchantAppointmentChange(appointment, { kind: 'cancelled' });
}

/** Durée (minutes) d'un service de réservation. */
async function serviceDuration(serviceId: number): Promise<number> {
  try {
    const { data } = await supabase
      .from('booking_services')
      .select('duration')
      .eq('id', serviceId)
      .maybeSingle();
    return Number(data?.duration) || 30;
  } catch {
    return 30;
  }
}

export interface RescheduleOutcome {
  ok: boolean;
  reason?: 'no_slot' | 'closed' | 'error';
  alternatives?: string[];
}

/**
 * Déplace un rendez-vous vers une nouvelle date/heure, après vérification du
 * calendrier ZCalendar (dispos, pauses, congés, créneaux déjà pris). Si le
 * créneau visé n'est pas libre, renvoie les créneaux les plus proches.
 */
export async function rescheduleClientAppointment(
  appointment: ClientAppointment,
  targetDate: string,
  targetTime: string,
): Promise<RescheduleOutcome> {
  const duration = await serviceDuration(appointment.serviceId);
  const svc = {
    id: appointment.serviceId,
    owner: 0,
    service_name: appointment.serviceName,
    duration,
    location: null,
    description: null,
  } as BookingService;

  let data: ServiceCalendar;
  try {
    data = await loadServiceBookingData(appointment.serviceId);
  } catch {
    return { ok: false, reason: 'error' };
  }

  if (data.holidays.some((h) => targetDate >= h.date_from && targetDate <= h.date_to)) {
    return { ok: false, reason: 'closed', alternatives: [] };
  }
  if (data.availabilities.length === 0) {
    return { ok: false, reason: 'closed', alternatives: [] };
  }

  // On ignore le rendez-vous en cours de déplacement dans le calcul des
  // créneaux occupés (sinon il bloquerait sa propre nouvelle place).
  const others = data.appointments.filter((a) => a.id !== appointment.id);
  const slots = computeAvailableSlots(targetDate, svc, data.availabilities, data.breaks, others);

  if (slots.length === 0) {
    const nextDates = await findNextDates(
      { id: appointment.serviceId, name: appointment.serviceName, duration, owner: null, merchantName: appointment.merchantName },
      targetDate,
    );
    return { ok: false, reason: 'no_slot', alternatives: nextDates.flatMap((n) => n.slots) };
  }

  if (!slots.includes(targetTime)) {
    const targetMin = toMinutes(targetTime);
    const sorted = [...slots].sort((a, b) => Math.abs(toMinutes(a) - targetMin) - Math.abs(toMinutes(b) - targetMin));
    return { ok: false, reason: 'no_slot', alternatives: sorted.slice(0, 4) };
  }

  const { error } = await supabase
    .from('booking_appointments')
    .update({ date: targetDate, time: `${targetTime}:00` })
    .eq('id', appointment.id);
  if (error) return { ok: false, reason: 'error' };

  // Le commerce est prévenu du nouveau créneau (cloche + e-mail, best-effort).
  void notifyMerchantAppointmentChange(appointment, {
    kind: 'rescheduled',
    newDate: targetDate,
    newTime: targetTime,
  });

  return { ok: true };
}

// ── Analyse de la date & l'heure demandées ──────────────────

const DAY_NAMES: Record<string, number> = {
  lundi: 1, mardi: 2, mercredi: 3, jeudi: 4, vendredi: 5, samedi: 6, dimanche: 7,
};

export interface ParsedWhen {
  date: string | null;
  time: string | null;
  dayLabel: string | null;
}

function currentDayIndex(): number {
  const d = new Date().getDay();
  return d === 0 ? 7 : d;
}

/** Extrait le jour et l'heure cités dans le message (relatif à aujourd'hui). */
export function parseRequestedWhen(text: string): ParsedWhen {
  const t = text.toLowerCase();

  // Heure : « 17h30 », « 17h », « 17:30 », « 9 heures »
  let time: string | null = null;
  const hm = t.match(/(\d{1,2})\s*[h:]\s*(\d{2})/);
  if (hm) {
    const h = parseInt(hm[1], 10);
    const m = parseInt(hm[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } else {
    const hOnly = t.match(/(\d{1,2})\s*(?:h|heures?|:)\b/);
    if (hOnly) {
      const h = parseInt(hOnly[1], 10);
      if (h >= 0 && h <= 23) time = `${String(h).padStart(2, '0')}:00`;
    }
  }

  // Jour : aujourd'hui / demain / nom de jour
  let targetDay: number | null = null;
  let dayLabel: string | null = null;
  const isToday = /aujourd'?hui/.test(t);
  const isTomorrow = /demain/.test(t);
  if (isToday) { targetDay = currentDayIndex(); dayLabel = "aujourd'hui"; }
  else if (isTomorrow) { const d = new Date(); d.setDate(d.getDate() + 1); targetDay = d.getDay() === 0 ? 7 : d.getDay(); dayLabel = 'demain'; }
  else {
    for (const [name, idx] of Object.entries(DAY_NAMES)) {
      if (new RegExp(`\\b${name}\\b`).test(t)) { targetDay = idx; dayLabel = name; break; }
    }
  }

  let date: string | null = null;
  if (targetDay != null) {
    const now = new Date();
    let diff = targetDay - currentDayIndex();
    if (diff < 0) diff += 7;
    const cand = new Date(now);
    cand.setDate(now.getDate() + diff);
    if (diff === 0 && time) {
      const [hh, mm] = time.split(':').map(Number);
      if (hh * 60 + mm <= now.getHours() * 60 + now.getMinutes()) cand.setDate(cand.getDate() + 7);
    }
    date = toDateStr(cand);
  }

  return { date, time, dayLabel };
}

// ── Chargement du calendrier ZCalendar d'un service ─────────

interface ServiceCalendar {
  availabilities: BookingAvailability[];
  breaks: BookingBreak[];
  holidays: BookingHoliday[];
  appointments: BookingAppointment[];
}

export async function loadServiceBookingData(serviceId: number): Promise<ServiceCalendar> {
  const [avRes, brRes, hoRes, apRes, avGlobal, brGlobal, hoGlobal] = await Promise.all([
    supabase.from('booking_availabilities').select('*').eq('service_id', serviceId),
    supabase.from('booking_breaks').select('*').eq('service_id', serviceId),
    supabase.from('booking_holidays').select('*').eq('service_id', serviceId),
    supabase.from('booking_appointments').select('id, date, time').eq('service_id', serviceId).neq('status', 'cancelled'),
    supabase.from('booking_availabilities').select('*').eq('service_id', 0),
    supabase.from('booking_breaks').select('*').eq('service_id', 0),
    supabase.from('booking_holidays').select('*').eq('service_id', 0),
  ]);

  const avMap = new Map<string, BookingAvailability>();
  ((avGlobal.data || []) as BookingAvailability[]).forEach((a) => avMap.set(a.day_of_week, a));
  ((avRes.data || []) as BookingAvailability[]).forEach((a) => avMap.set(a.day_of_week, a));

  const brMap = new Map<string, BookingBreak>();
  ((brGlobal.data || []) as BookingBreak[]).forEach((b) => brMap.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));
  ((brRes.data || []) as BookingBreak[]).forEach((b) => brMap.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));

  const hoMap = new Map<string, BookingHoliday>();
  ((hoGlobal.data || []) as BookingHoliday[]).forEach((h) => hoMap.set(`${h.date_from}-${h.date_to}`, h));
  ((hoRes.data || []) as BookingHoliday[]).forEach((h) => hoMap.set(`${h.date_from}-${h.date_to}`, h));

  return {
    availabilities: Array.from(avMap.values()),
    breaks: Array.from(brMap.values()),
    holidays: Array.from(hoMap.values()),
    appointments: (apRes.data || []) as BookingAppointment[],
  };
}

// ── Réservation ──────────────────────────────────────────────

export interface BookableService {
  id: number;
  name: string;
  duration: number;
  owner?: number | null;
  merchantName?: string | null;
}

export interface BookingOutcome {
  ok: boolean;
  time: string | null;
  serviceName: string | null;
  merchantName: string | null;
  date: string;
  reason?: 'no_calendar' | 'no_slot' | 'no_alternative' | 'error';
  alternatives?: string[];
  nextDates?: { date: string; slots: string[] }[];
}

function toMinutes(t: string): number {
  try { return parseTime(t).total; } catch { return 720; }
}

async function insertAppointment(
  service: BookableService,
  date: string,
  time: string,
  user: { id: number; name: string; email: string; telephone?: string },
): Promise<void> {
  const { error } = await supabase.from('booking_appointments').insert({
    service_id: service.id,
    fullname: user.name || 'Client',
    email: user.email || '',
    phone: user.telephone || '',
    message: "Réservation prise via l'assistant IA Zifek",
    date,
    time: `${time}:00`,
    status: 'confirmed',
  });
  if (error) throw error;

  // 🔔 Cloche + notification PWA du marchand (avec son d'alerte côté layout).
  if (service.owner) {
    void pushNotification({
      userId: service.owner,
      type: 'booking',
      message: `Nouvelle réservation : ${service.name}, le ${date} à ${time} (${user.name || 'Client'}).`,
    });
  }

  // Notification e-mail best-effort au marchand (n'empêche jamais la réservation).
  try {
    if (service.owner) {
      const { data: owner } = await supabase
        .from('users')
        .select('email')
        .eq('id', service.owner)
        .maybeSingle();
      const to = (owner?.email as string) || '';
      if (to) {
        void supabase.functions.invoke('send-booking-notification', {
          body: {
            to,
            type: 'new_booking',
            service_name: service.name,
            customer_name: user.name || 'Client',
            customer_email: user.email || '',
            customer_phone: user.telephone || '',
            customer_message: "Réservation via l'assistant IA",
            date,
            time,
            duration: service.duration,
          },
        });
      }
    }
  } catch { /* best-effort */ }
}

/** Cherche les prochaines dates avec au moins un créneau (max ~3 dates). */
async function findNextDates(
  service: BookableService,
  fromDate: string,
  count = 3,
): Promise<{ date: string; slots: string[] }[]> {
  const svc = { id: service.id, owner: service.owner || 0, service_name: service.name, duration: service.duration || 30, location: null, description: null } as BookingService;
  let data: ServiceCalendar;
  try { data = await loadServiceBookingData(service.id); } catch { return []; }

  const out: { date: string; slots: string[] }[] = [];
  const base = new Date(fromDate);
  const todayStr = toDateStr(new Date());
  for (let i = 1; i <= 30 && out.length < count; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const ds = toDateStr(d);
    if (ds < todayStr) continue;
    if (data.holidays.some((h) => ds >= h.date_from && ds <= h.date_to)) continue;
    const slots = computeAvailableSlots(ds, svc, data.availabilities, data.breaks, data.appointments);
    if (slots.length > 0) out.push({ date: ds, slots: slots.slice(0, 4) });
  }
  return out;
}

/**
 * Tente de réserver le créneau demandé sur la meilleure offre disponible.
 * Parcourt les services par ordre de pertinence et retient le premier qui a
 * un calendrier ZCalendar. Si l'heure exacte est libre → réservation immédiate.
 * Sinon, propose les créneaux les plus proches (même jour puis jours suivants).
 */
export async function attemptBookingAcross(
  services: BookableService[],
  date: string,
  requestedTime: string | null,
  user: { id: number; name: string; email: string; telephone?: string },
): Promise<BookingOutcome> {
  const candidates = services.slice(0, 4);
  let bestAlt: { service: BookableService; slots: string[] } | null = null;
  let anyCalendar = false;
  let firstWithCalendar: BookableService | null = null;

  for (const s of candidates) {
    const svc = { id: s.id, owner: s.owner || 0, service_name: s.name, duration: s.duration || 30, location: null, description: null } as BookingService;
    let data: ServiceCalendar;
    try { data = await loadServiceBookingData(s.id); } catch { continue; }
    if (data.availabilities.length === 0) continue;
    anyCalendar = true;
    if (!firstWithCalendar) firstWithCalendar = s;

    const slots = computeAvailableSlots(date, svc, data.availabilities, data.breaks, data.appointments);

    if (requestedTime && slots.includes(requestedTime)) {
      try {
        await insertAppointment(s, date, requestedTime, user);
        return { ok: true, time: requestedTime, serviceName: s.name, merchantName: s.merchantName ?? null, date };
      } catch {
        return { ok: false, time: null, serviceName: s.name, merchantName: s.merchantName ?? null, date, reason: 'error' };
      }
    }

    if (slots.length > 0) {
      const target = requestedTime ? toMinutes(requestedTime) : 720;
      const sorted = [...slots].sort((a, b) => Math.abs(toMinutes(a) - target) - Math.abs(toMinutes(b) - target));
      if (!bestAlt || Math.abs(toMinutes(sorted[0]) - target) < Math.abs(toMinutes(bestAlt.slots[0]) - target)) {
        bestAlt = { service: s, slots: sorted.slice(0, 3) };
      }
    }
  }

  if (bestAlt) {
    return {
      ok: false,
      time: null,
      serviceName: bestAlt.service.name,
      merchantName: bestAlt.service.merchantName ?? null,
      date,
      reason: 'no_slot',
      alternatives: bestAlt.slots,
    };
  }

  if (!anyCalendar) {
    return { ok: false, time: null, serviceName: candidates[0]?.name ?? null, merchantName: candidates[0]?.merchantName ?? null, date, reason: 'no_calendar' };
  }

  const nextDates = firstWithCalendar ? await findNextDates(firstWithCalendar, date) : [];
  return {
    ok: false,
    time: null,
    serviceName: firstWithCalendar?.name ?? candidates[0]?.name ?? null,
    merchantName: firstWithCalendar?.merchantName ?? null,
    date,
    reason: 'no_alternative',
    nextDates,
  };
}

// ── Rendez-vous du client ────────────────────────────────────

export interface ClientAppointment {
  id: number;
  serviceId: number;
  serviceName: string;
  merchantName: string | null;
  ville: string | null;
  date: string;
  time: string;
  status: string;
}

/** Rendez-vous à venir du client (identifié par email / téléphone). */
export async function fetchClientAppointments(email: string, telephone?: string): Promise<ClientAppointment[]> {
  const filters: string[] = [];
  if (email) filters.push(`email.eq.${email}`);
  if (telephone) filters.push(`phone.eq.${telephone}`);
  if (filters.length === 0) return [];

  const { data } = await supabase
    .from('booking_appointments')
    .select('id, service_id, date, time, status, email')
    .or(filters.join(','))
    .neq('status', 'cancelled')
    .order('date', { ascending: true });

  const rows = (data || []) as { id: number; service_id: number; date: string; time: string; status: string }[];
  if (rows.length === 0) return [];

  const serviceIds = Array.from(new Set(rows.map((r) => r.service_id).filter(Boolean)));
  const svcMap: Record<number, { name: string; owner: number | null }> = {};
  if (serviceIds.length > 0) {
    const { data: svcRows } = await supabase
      .from('booking_services')
      .select('id, service_name, owner')
      .in('id', serviceIds);
    (svcRows || []).forEach((s: { id: number; service_name: string; owner: number }) => {
      svcMap[s.id] = { name: s.service_name, owner: s.owner };
    });
  }

  const ownerIds = Array.from(new Set(Object.values(svcMap).map((s) => s.owner).filter((v): v is number => typeof v === 'number')));
  const ownerMap: Record<number, { name: string; ville: string | null }> = {};
  if (ownerIds.length > 0) {
    const { data: owners } = await supabase
      .from('users')
      .select('id, name, nomcommerce, Ville')
      .in('id', ownerIds);
    (owners || []).forEach((o: { id: number; name: string; nomcommerce: string; Ville: string }) => {
      ownerMap[o.id] = { name: o.nomcommerce || o.name, ville: o.Ville || null };
    });
  }

  const nowStr = toDateStr(new Date());
  return rows
    .filter((r) => r.date >= nowStr)
    .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))
    .map((r) => {
      const svc = svcMap[r.service_id];
      const owner = svc?.owner != null ? ownerMap[svc.owner] : undefined;
      return {
        id: r.id,
        serviceId: r.service_id,
        serviceName: svc?.name || 'Rendez-vous',
        merchantName: owner?.name || null,
        ville: owner?.ville || null,
        date: r.date,
        time: (r.time || '').slice(0, 5),
        status: r.status,
      };
    });
}

/** Formate un rendez-vous pour l'affichage dans le fil du chat. */
export function formatAppointmentLine(a: ClientAppointment): string {
  const place = [a.merchantName, a.ville].filter(Boolean).join(', ');
  return `• ${formatDateFr(a.date)} à ${a.time} — ${a.serviceName}${place ? ` (${place})` : ''}`;
}